/**
 * Hook V2 — useApprenant + useApprenantMutation
 *
 * Phase wiring 3 (27/05/2026) : édition réelle via PATCH /v2/apprenants/:id.
 *
 *   - useApprenant(id) → GET (avec fallback mock)
 *   - useApprenantMutation(id) → PATCH (optimistic update + invalidation auto)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../../lib/api.js';

export interface Apprenant {
  id: string;
  numeroDossier: string | null;
  civilite: 'M.' | 'Mme' | 'Mx' | null;
  nom: string;
  prenom: string;
  email: string;
  telephone: string | null;
  formation: { id: string | null; nom: string; nomCommercial: string } | null;
  niveauActuel: string | null;
  statut: 'actif' | 'inactif' | 'abandon';
  pipelineStage: 'Inscrit' | 'Début' | 'Milieu' | 'Fin' | 'Terminé' | 'Alerte' | 'Abandon' | null;
  dateInscription: string | null;
  dateSortie: string | null;
  entreprise: { id: string | null; name: string } | null;
  formateur: { id: string | null; name: string } | null;
  typeFinancement: string | null;
  montantFormation: number | null;
  heuresSynchrones: number | null;
  heuresAsynchrones: number | null;
  heuresRealisees: number | null;
  heuresGlobales: number | null;
  pctAssiduite: number | null;
  bpfEligible: boolean;
  eleveCPF: boolean;
  handicap: boolean;
  activityVertical: string | null;
  lieuFormation: string | null;

  // V2 extension
  dateNaissance: string | null;
  adresse: string | null;
  situationProfessionnelle: string | null;
  emailSecondaire: string | null;
  niveauVise: string | null;
  objectifCertification: string | null;
  objectifType: string | null;
  objectifDetail: string | null;
  formationAnterieure: boolean;
  ancienStagiaire: boolean;
  eLearningAdapte: boolean;
  milieuDate: string | null;
  finDate: string | null;
  source: string | null;
  dossierComplet: string | null;
  complianceFlag: string | null;
  qualiopiTracked: boolean;
  emailOptOut: boolean;
  moodleUserId: string | null;
  commentaires: string | null;

  /**
   * OF expéditeur dérivé du segment (activityVertical) :
   *  - CPF/OPCO/Corporate → RK Formations
   *  - Andalous Private → Andalous e-Learning
   * Donnée read-only calculée côté API (cf. lib/of-sender.ts).
   * Utilisée pour les envois email et l'affichage UI.
   */
  ofSender: {
    scope: 'rk' | 'andalous';
    name: string;
    email: string;
  };
}

/** Shape du body PATCH — clés alignées avec ApprenantPatchInput backend. */
export interface ApprenantPatchBody {
  nom?: string;
  prenom?: string;
  email?: string;
  telephone?: string;
  niveau?: string;
  civilite?: string;
  numeroDossier?: string;
  dateEntree?: string;
  dateSortie?: string;
  montantFormation?: number;
  eleveCpf?: boolean;
  bpfEligible?: boolean;
  lieuFormation?: string;
  typeFinancement?: string;
  nombreHeuresGlobal?: number;
  heuresSynchrones?: number;
  heuresAsynchrones?: number;
  activityVertical?: string;
  statutZoho?: string;
  handicap?: boolean;
  formationId?: string;
  entrepriseId?: string;
  formateurId?: string;
  // V2 extension
  dateNaissance?: string;
  adresse?: string;
  situationProfessionnelle?: string;
  emailSecondaire?: string;
  niveauVise?: string;
  objectifCertification?: string;
  objectifType?: string;
  objectifDetail?: string;
  formationAnterieure?: boolean;
  ancienStagiaire?: boolean;
  eLearningAdapte?: boolean;
  source?: string;
  dossierComplet?: string;
  complianceFlag?: string;
  qualiopiTracked?: boolean;
  emailOptOut?: boolean;
  moodleUserId?: string;
  commentaires?: string;
}

const MARTINE_MOCK: Apprenant = {
  id: 'demo-martine',
  numeroDossier: '401605976412',
  civilite: 'Mme',
  nom: 'VILLARD',
  prenom: 'MARTINE',
  email: 'martine.villard@example.com',
  telephone: '06 12 34 56 78',
  formation: {
    id: 'ara-30h-lilate',
    nom: 'ara-personalise-90h-lilate',
    nomCommercial: 'Arabe LILATE — 30h CPF',
  },
  niveauActuel: 'A2',
  statut: 'actif',
  pipelineStage: 'Milieu',
  dateInscription: '2025-04-15',
  dateSortie: null,
  entreprise: null,
  formateur: { id: 'rafik', name: 'Rafik KERMACHE' },
  typeFinancement: 'CPF',
  montantFormation: 5100,
  heuresSynchrones: 30,
  heuresAsynchrones: 60,
  heuresRealisees: 54,
  heuresGlobales: 90,
  pctAssiduite: 60,
  bpfEligible: true,
  eleveCPF: true,
  handicap: false,
  activityVertical: 'CPF (State Funded)',
  lieuFormation: 'À distance',
  dateNaissance: null,
  adresse: null,
  situationProfessionnelle: null,
  emailSecondaire: null,
  niveauVise: null,
  objectifCertification: null,
  objectifType: null,
  objectifDetail: null,
  formationAnterieure: false,
  ancienStagiaire: false,
  eLearningAdapte: false,
  milieuDate: null,
  finDate: null,
  source: null,
  dossierComplet: null,
  complianceFlag: null,
  qualiopiTracked: false,
  emailOptOut: false,
  moodleUserId: null,
  commentaires: null,
  // Martine = CPF → RK Formations (donnée dérivée, ici hardcodée pour le mock)
  ofSender: {
    scope: 'rk',
    name: 'RK Formations',
    email: 'contact@rkformations.fr',
  },
};

async function fetchApprenant(id: string): Promise<Apprenant & { source: 'api' | 'mock' }> {
  try {
    const data = await api.get<Apprenant>(`/api/v1/v2/apprenants/${id}`);
    return { ...data, source: 'api' };
  } catch (err) {
    if (err instanceof ApiClientError && [401, 404, 0].includes(err.status)) {
      return { ...MARTINE_MOCK, source: 'mock' };
    }
    return { ...MARTINE_MOCK, source: 'mock' };
  }
}

export function useApprenant(id: string): {
  data: (Apprenant & { source: 'api' | 'mock' }) | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ['v2', 'apprenant', id],
    queryFn: () => fetchApprenant(id),
    staleTime: 30_000,
  });
  return { data: data ?? null, isLoading, error: (error as Error | null) ?? null };
}

/**
 * Hook de mutation pour éditer un apprenant.
 *
 * Utilisation :
 *   const mutate = useApprenantMutation('demo-martine');
 *   await mutate({ email: 'new@example.com' });
 *
 * Côté EditableField : passer `onSave={(v) => mutate({ email: v })}`.
 *
 * En cas de succès, invalide la query ['v2', 'apprenant', id] → re-fetch auto.
 * En cas d'échec, throw l'erreur pour que le composant l'affiche.
 */
export function useApprenantMutation(
  id: string,
): (patch: ApprenantPatchBody) => Promise<Apprenant> {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (patch: ApprenantPatchBody) =>
      api.patch<Apprenant>(`/api/v1/v2/apprenants/${id}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['v2', 'apprenant', id] });
    },
  });
  return mutation.mutateAsync;
}
